<template>
  <div id="app">
    <Navigator />

    <main class="app__container">
      <transition
        @enter="enter"
        @leave="leave"
        :appear="true"
        mode="out-in"
      >
        <router-view :key="$route.fullPath"/>
      </transition>
    </main>

    <Grain />

    <CookieLaw
      theme="min"
      button-text="Ok"
      button-class="cookie-button"
    >
      <div slot="message">
        <bdi>This website uses cookies.</bdi>
        <br class="m-s">
        <a
          href="https://www.iubenda.com/privacy-policy/10208006"
          target="_blank"
          rel="noopener"
          class="fx"
          aria-label="Read more about privacy policy"
        >
          Learn More
        </a>
      </div>
    </CookieLaw>
  </div>
</template>

<script>
import CookieLaw from 'vue-cookie-law';
import TweenMax from 'gsap/TweenMax';
import loop from '@/scripts/loop';
import initialStates from '@/scripts/initialStates';
import animations from '@/scripts/animations';
import { qsa } from '@/scripts/utils';

import Navigator from '@/components/Navigator.vue';
import Grain from '@/components/Grain.vue';


export default {
  name: 'app',

  components: {
    CookieLaw,
    Navigator,
    Grain,
  },

  data() {
    return {
      loopIsRunning: false,
    };
  },

  created() {
    loop.start();
  },

  methods: {
    initializeAnimations(ctx, callback) {
      const label = `animations-${ctx.getAttribute('data-page')}`;
      const els = qsa('[data-anim]', ctx);

      if (els.length > 0) {
        const elsToSet = [];

        els.forEach((el) => {
          const obj = this.createObjToAnimate(el);
          elsToSet.push(obj);

          initialStates.get(obj.animation)(obj);
        });

        const handlerAnimations = () => this.addAnimationsPage(elsToSet, label);
        loop.add(handlerAnimations, label, true);

        if (loop.stopped) loop.start();
        this.loopIsRunning = true;
      }

      callback();
    },

    createObjToAnimate(el) {
      const obj = {
        node: el,
        initFromTop: el.getBoundingClientRect().top,
        fromTop() {
          return this.node.getBoundingClientRect().top;
        },
        height: el.getBoundingClientRect().height,
        animation: el.getAttribute('data-anim'),
        isAnimated: false,
        isVisible: false,
        splitted: false,
      };

      return obj;
    },

    addAnimationsPage(els, label) {
      els.forEach((el, i) => {
        if (!el.isAnimated && el.fromTop() < (window.innerHeight - 150)) {
          el.isAnimated = true; // eslint-disable-line
          el.isVisible = true; // eslint-disable-line

          animations.get(el.animation)(el);
        } else if (el.isVisible
          && ((el.fromTop() + el.height) < 0 || el.fromTop() > window.innerHeight)) {
          el.isVisible = false; // eslint-disable-line

          if (els.length === 1) {
            loop.remove(label);
            this.loopIsRunning = false;
          }

          if (el.splitted) el.instance.revert();
          els.splice(i, 1);
        }
      });
    },

    enter(el, done) {
      console.log('enter', el);
      TweenMax.fromTo(el, 0.5, { autoAlpha: 0 }, { autoAlpha: 1 });
      this.initializeAnimations(el, done);
    },

    leave(el, done) {
      console.log('leave', el);
      const page = el.getAttribute('data-page');
      if (this.loopIsRunning) loop.remove(`add-animation-${page}`);
      TweenMax.to(el, 0.5, {
        autoAlpha: 0,
        onComplete: () => {
          document.body.scrollTop = 0;
          done();
        },
      });
    },
  },
};
</script>


<style lang="scss">
@import './applications';

#app {
  display: block;
  position: relative;
  width: 100vw;
}

#app main {
  display: block;
  max-width: 100vw;
  width: 100%;
  overflow: hidden;
}

.c-container {
  position: relative;
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 60px 20px;

  box-sizing: border-box;

  @include mq(mobileL) {
    padding: 80px 60px 80px 60px;
  }

  @include mq(dm) {
    max-width: 1440px;
  }

  @include mq(dl) {
    max-width: 1680px;
  }

  @include mq(dxl) {
    max-width: 1920px;
  }
}

.Cookie.Cookie--min {
  position: fixed;
  width: 100%;
  padding: 6px 20px;

  background: $color-bg;
  color: $color-text;
  border: 1px solid $color-black;
  font-size: 1em;

  z-index: 80;

  display: flex;
  flex-flow: row;

  overflow: visible;

  transition: all 0.8s ease-out;

  transition-delay: 1s;

  @include mq(ipadP) {
    width: 40%;
    max-width: 500px;
    margin: 20px auto;
    padding: 15px 25px;
  }

  @include mq(ds) {
    transition-delay: 0s;
  }

  a {
    color: $color-black;
  }

  bdi {
    opacity: 0.75;
  }

  .Cookie__buttons {
    font-size: 1em;
    margin-left: 20px;

    padding: 0;
    font-weight: 400;

    transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);

    cursor: pointer;

    @include mq(ipadP) {
      padding: 0;
    }

    .cookie-button {
      z-index: 2;
      position: relative;
      overflow: hidden;
      cursor: pointer;

      appearance: none;
      box-shadow: none;
      border: 0;

      padding: 8px 22px;
      margin: 0px !important;

      color: $color-text;
      border: 1px solid $color-black;
      background: $color-bg;

      transition: all 0.2s ease-out;

      &:hover {
        border-color: $color-text;
      }

      @include mq(ipadP) {
        padding: 8px 24px;
      }
    }
  }
}
</style>
